import React, { useEffect, useMemo, useRef, useState } from 'react';
import { searchObjects, MATCHES_SHOWN } from './ObjectPicker';

// One control to pick a name from a list (4.2.8, 2026-11-05): a search box whose matches — the list
// ALPHABETISED, case-insensitively — drop down under it as you type; click one (or Enter for the first)
// to pick it. The rule dialog's column and source pickers: a <select> over a few hundred profiled columns
// in profile order was the painful part of editing a rule. Same contract as ObjectPicker, over plain
// strings and without the kind filters: the value changes only when a match is picked or the box is
// cleared; other text is the user still searching, put back to the value when the box loses focus. An
// empty `options` takes free text (a column list that has not loaded, or a source without a profile).

const lower = (s) => String(s || '').toLowerCase();
const byName = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });

/** `options` sorted alphabetically, case-insensitively, without duplicates. */
export const sortedOptions = (options) => Array.from(new Set((options || []).filter(Boolean).map(String))).sort(byName);

const SearchPick = ({ value, onChange, options, disabled, placeholder, testId, label }) => {
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => { setText(value || ''); }, [value]);
  const names = useMemo(() => sortedOptions(options), [options]);
  const free = names.length === 0;
  // The box holding the picked value is not a search yet: focusing it shows everything to browse.
  const query = lower(text.trim()) === lower(value || '') ? '' : text;
  const matches = useMemo(() => searchObjects(names, query), [names, query]);
  const searching = open && !free;

  const pick = (name) => {
    setText(name);
    setOpen(false);
    if (name !== value) onChange(name);
  };
  const edit = (v) => {
    setText(v);
    setOpen(true);
    if (free) { onChange(v.trim()); return; }
    if (!v.trim() && value) onChange('');
  };
  const leave = () => {
    setOpen(false);
    if (free) return;
    const t = text.trim();
    const hit = names.find((n) => lower(n) === lower(t));
    if (hit) { if (hit !== value) onChange(hit); } else if (t) setText(value || '');
  };
  const keyDown = (e) => {
    if (free) return;
    if (e.key === 'Enter' && matches.length > 0 && text.trim()) { e.preventDefault(); pick(matches[0]); }
    if (e.key === 'Escape') { setOpen(false); setText(value || ''); }
  };

  return (
    <span className='dg-picker dg-picker--pick' ref={box}
      onBlur={(e) => { if (!box.current || !box.current.contains(e.relatedTarget)) leave(); }}>
      <input className='dg-input' value={text} disabled={disabled} aria-label={label} autoComplete='off'
        role={free ? undefined : 'combobox'} aria-expanded={searching} aria-autocomplete={free ? undefined : 'list'}
        placeholder={free ? placeholder : `Search ${names.length.toLocaleString()}…`}
        onChange={(e) => edit(e.target.value)} onFocus={() => setOpen(true)} onKeyDown={keyDown}
        data-testid={testId} />
      {searching && !disabled && (
        <div className='dg-picker__menu' role='listbox' data-testid={`${testId}-matches`}>
          {matches.length === 0 && <div className='dg-picker__none' data-testid={`${testId}-none`}>Nothing matches “{text.trim()}”.</div>}
          {matches.slice(0, MATCHES_SHOWN).map((n) => (
            <button type='button' role='option' aria-selected={n === value} key={n} className={`dg-picker__item${n === value ? ' dg-picker__item--on' : ''}`}
              onMouseDown={(e) => e.preventDefault()} onClick={() => pick(n)} data-testid={`${testId}-match-${n}`}>
              <span className='dg-mono'>{n}</span>
            </button>
          ))}
          {matches.length > MATCHES_SHOWN && (
            <div className='dg-picker__none' data-testid={`${testId}-more`}>{(matches.length - MATCHES_SHOWN).toLocaleString()} more — keep typing.</div>
          )}
        </div>
      )}
    </span>
  );
};

export default SearchPick;
